import type { EffectivePriorityCtx, MatrixPos, MatrixPriorityTask, Priority, Task } from "@app/core";
import { clamp01, deadlineUrgencyLevel, effectiveMatrixDisplayPos, effectivePriorityOf, matrixCellCenter, matrixDisplayPos, matrixEnabledFor } from "@app/core";
import { systemTimeZone } from "@app/core";
import { useMemo } from "react";
import { useToday } from "../calendar-view/useNowMinute.js";
import { useSettingsStore } from "../../stores/index.js";
import { matrixAccentFromPos } from "../task-colors.js";

/** The task fields the effective matrix display depends on. */
export type MatrixDisplayTask = Pick<Task, "matrix_pos" | "priority" | "deadline_at" | "due_at" | "tz">;

/**
 * Returns `(task) => MatrixPos | null` — the matrix DISPLAY position with
 * deadline-derived urgency folded in (see `effectiveMatrixDisplayPos`). An
 * unplaced, flagged task with a deadline ghosts at its derived urgency column;
 * a placed task keeps its stored spot. Re-derives at the day boundary (urgency
 * only changes per calendar day). Deadline source is `deadline_at`, falling back
 * to the scheduled `due_at`.
 */
export function useEffectiveMatrixDisplay(): (task: MatrixDisplayTask) => MatrixPos | null {
  const defaultTz = useSettingsStore((s) => s.defaultTz);
  const enabled = useSettingsStore((s) => s.deadlineUrgency);
  const highDays = useSettingsStore((s) => s.deadlineUrgencyHighDays);
  const mediumDays = useSettingsStore((s) => s.deadlineUrgencyMediumDays);
  const today = useToday();

  return useMemo(() => {
    const now = new Date(today);
    now.setHours(0, 0, 0, 0);
    const tz = defaultTz ?? systemTimeZone();
    const cfg = { enabled, highWithinDays: highDays, mediumWithinDays: mediumDays };
    return (task: MatrixDisplayTask) =>
      effectiveMatrixDisplayPos(task.matrix_pos, task.priority, task.deadline_at ?? task.due_at, now, task.tz ?? tz, cfg);
  }, [today, defaultTz, enabled, highDays, mediumDays]);
}

/** The task fields the continuous matrix priority color depends on. */
export type MatrixColorTask = MatrixDisplayTask & Pick<Task, "is_event">;

/**
 * Continuous matrix-diagonal priority color (green→red) for a task's block/
 * chip, or null. Non-null only when the matrix system covers the task
 * (`matrixEnabledFor`) AND the task is actually PLACED (`matrix_pos != null`)
 * — the same placed-gate as {@link effectivePriorityOf}, so the rendered color
 * can never land in a different band than the priority filter/sort put the
 * task in. Unplaced flag-only tasks keep their discrete flag rendering.
 */
export function useMatrixPriorityColor(task: MatrixColorTask | null | undefined): string | null {
  const isEvent = !!task?.is_event;
  const active = useSettingsStore((s) => matrixEnabledFor(s.matrixScope, isEvent));
  const displayOf = useEffectiveMatrixDisplay();
  return task && active && task.matrix_pos ? matrixAccentFromPos(displayOf(task)) : null;
}

/** Cell-center urgency value for a discrete level; null for "none". */
function urgencyValueOf(level: Priority): number | null {
  const col = level === "high" ? 2 : level === "medium" ? 1 : level === "low" ? 0 : null;
  return col === null ? null : matrixCellCenter(col);
}

/**
 * The urgency value (0..1) a live-edited FORM's deadline implies, or null when
 * derivation is disabled / there is no deadline. `deadlineIso` is the form's
 * `deadline_at ?? due_at` composed instant (see `formDeadlineIso`).
 */
export function useDeadlineUrgencyValue(deadlineIso: string | null, tz: string | null): number | null {
  const defaultTz = useSettingsStore((s) => s.defaultTz);
  const enabled = useSettingsStore((s) => s.deadlineUrgency);
  const highDays = useSettingsStore((s) => s.deadlineUrgencyHighDays);
  const mediumDays = useSettingsStore((s) => s.deadlineUrgencyMediumDays);
  const today = useToday();
  return useMemo(() => {
    const now = new Date(today);
    now.setHours(0, 0, 0, 0);
    const zone = tz ?? defaultTz ?? systemTimeZone();
    const level = deadlineUrgencyLevel(deadlineIso, now, zone, { enabled, highWithinDays: highDays, mediumWithinDays: mediumDays });
    return urgencyValueOf(level);
  }, [deadlineIso, tz, today, defaultTz, enabled, highDays, mediumDays]);
}

/**
 * Display position for the matrix widget with deadline-derived urgency folded
 * in: a placed task with AUTO urgency shows its dot at `deadlineUrgency` (keeping
 * importance); a manual placement keeps its stored spot; an unplaced FLAGGED
 * task ghosts at `deadlineUrgency` (else neutral center). Shared by the picker
 * widget, its rail bookmark and the mobile sheet border.
 */
export function matrixDisplayWithUrgency(
  value: MatrixPos | null,
  priority: Priority,
  deadlineUrgency: number | null,
): MatrixPos | null {
  if (value) {
    const urgency = value.urgencyAuto ? (deadlineUrgency ?? value.urgency) : value.urgency;
    return { urgency: clamp01(urgency), importance: clamp01(value.importance), urgencyAuto: !!value.urgencyAuto };
  }
  const base = matrixDisplayPos(null, priority);
  if (base && deadlineUrgency != null) return { ...base, urgency: deadlineUrgency };
  return base;
}

/** Day-anchored `EffectivePriorityCtx` (`@app/core`; consumed by
 *  `effectivePriorityOf`/`effectiveUrgencyOf`) bound to settings + the day
 *  boundary. */
export function useEffectivePriorityCtx(): EffectivePriorityCtx {
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const defaultTz = useSettingsStore((s) => s.defaultTz);
  const enabled = useSettingsStore((s) => s.deadlineUrgency);
  const highDays = useSettingsStore((s) => s.deadlineUrgencyHighDays);
  const mediumDays = useSettingsStore((s) => s.deadlineUrgencyMediumDays);
  const today = useToday();
  return useMemo(() => {
    const now = new Date(today);
    now.setHours(0, 0, 0, 0);
    return { now, defaultTz: defaultTz ?? systemTimeZone(), config: { enabled, highWithinDays: highDays, mediumWithinDays: mediumDays }, matrixScope };
  }, [today, matrixScope, defaultTz, enabled, highDays, mediumDays]);
}

/** Returns `(task) => Priority` — the live priority resolver (see
 *  {@link effectivePriorityOf}), bound to settings + the day boundary. */
export function useEffectivePriority(): (task: MatrixPriorityTask) => Priority {
  const ctx = useEffectivePriorityCtx();
  return useMemo(() => (task: MatrixPriorityTask) => effectivePriorityOf(task, ctx), [ctx]);
}
